import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeApi } from '@/lib/api';
import type {
  ListDocumentsQuery,
  UpdateDocumentRequest,
  UpdateConfigRequest,
  ExecuteExtractionRequest,
  LogsQuery,
  SkippedConversationDetail,
} from '@/types/knowledge';
import { useToast } from '@/hooks/use-toast';

const QUERY_KEY_DOCUMENTS = 'knowledge-documents';
const QUERY_KEY_DOCUMENT = 'knowledge-document';
const QUERY_KEY_VERSIONS = 'knowledge-versions';
const QUERY_KEY_CONFIG = 'knowledge-extraction-config';
const QUERY_KEY_LOGS = 'knowledge-extraction-logs';

/**
 * Fetch all knowledge documents with optional filters
 */
export function useKnowledgeDocuments(query?: ListDocumentsQuery) {
  return useQuery({
    queryKey: [QUERY_KEY_DOCUMENTS, query],
    queryFn: () => knowledgeApi.listDocuments(query).then((res) => res.data),
  });
}

/**
 * Fetch a single knowledge document by ID
 */
export function useKnowledgeDocument(id: string) {
  return useQuery({
    queryKey: [QUERY_KEY_DOCUMENT, id],
    queryFn: () => knowledgeApi.getDocument(id).then((res) => res.data),
    enabled: !!id,
  });
}

/**
 * Update a knowledge document
 */
export function useUpdateDocument(id: string) {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: (data: UpdateDocumentRequest) =>
      knowledgeApi.updateDocument(id, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_DOCUMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_DOCUMENT, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_VERSIONS, id] });
      success('Documento atualizado com sucesso');
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as unknown as { response?: { data?: { message?: string } } })?.response?.data?.message)
        : 'Erro ao atualizar documento';
      error(errorMessage || 'Erro ao atualizar documento');
    },
  });
}

/**
 * Fetch document version history
 */
export function useDocumentVersions(id: string) {
  return useQuery({
    queryKey: [QUERY_KEY_VERSIONS, id],
    queryFn: () => knowledgeApi.getVersions(id).then((res) => res.data),
    enabled: !!id,
  });
}

/**
 * Fetch a single document version
 */
export function useDocumentVersion(documentId: string, versionId: string) {
  return useQuery({
    queryKey: [QUERY_KEY_VERSIONS, documentId, versionId],
    queryFn: () => knowledgeApi.getVersion(documentId, versionId).then((res) => res.data),
    enabled: !!documentId && !!versionId,
  });
}

/**
 * Restore a document version
 */
export function useRestoreVersion(documentId: string) {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: (versionId: string) =>
      knowledgeApi.restoreVersion(documentId, versionId).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_DOCUMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_DOCUMENT, documentId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_VERSIONS, documentId] });
      success('Versão restaurada com sucesso');
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as unknown as { response?: { data?: { message?: string } } })?.response?.data?.message)
        : 'Erro ao restaurar versão';
      error(errorMessage || 'Erro ao restaurar versão');
    },
  });
}

/**
 * Fetch extraction configuration
 */
export function useExtractionConfig() {
  return useQuery({
    queryKey: [QUERY_KEY_CONFIG],
    queryFn: () => knowledgeApi.getConfig().then((res) => res.data),
  });
}

/**
 * Update extraction configuration
 */
export function useUpdateConfig() {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: (data: UpdateConfigRequest) =>
      knowledgeApi.updateConfig(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CONFIG] });
      success('Configuração atualizada com sucesso');
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as unknown as { response?: { data?: { message?: string } } })?.response?.data?.message)
        : 'Erro ao atualizar configuração';
      error(errorMessage || 'Erro ao atualizar configuração');
    },
  });
}

/**
 * Execute extraction manually
 */
export function useExecuteExtraction() {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: (data: ExecuteExtractionRequest) =>
      knowledgeApi.executeExtraction(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_LOGS] });
      success('Extração iniciada com sucesso');
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as unknown as { response?: { data?: { message?: string } } })?.response?.data?.message)
        : 'Erro ao iniciar extração';
      error(errorMessage || 'Erro ao iniciar extração');
    },
  });
}

/**
 * Fetch extraction logs (default: first page, 20 items)
 */
export function useExtractionLogs(query?: LogsQuery) {
  return useQuery({
    queryKey: [QUERY_KEY_LOGS, query],
    queryFn: () => knowledgeApi.getLogs(query || { page: 1, limit: 20 }).then((res) => res.data),
  });
}

/**
 * Fetch extraction logs with pagination
 */
export function useExtractionLogsPaginated(query: LogsQuery) {
  return useQuery({
    queryKey: [QUERY_KEY_LOGS, query],
    queryFn: () => knowledgeApi.getLogs(query).then((res) => res.data),
  });
}

/**
 * Fetch extraction log details
 */
export function useExtractionLogDetails(id: string) {
  return useQuery({
    queryKey: [QUERY_KEY_LOGS, 'details', id],
    queryFn: () => knowledgeApi.getLogDetails(id).then((res) => res.data),
    enabled: !!id,
  });
}

/**
 * Export extraction log documents as CSV
 */
export function useExportLogCSV() {
  const { success, error } = useToast();

  return useMutation({
    mutationFn: async (logId: string) => {
      const response = await knowledgeApi.exportLogCsv(logId);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `knowledge-log-${logId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      success('CSV exportado com sucesso');
    },
    onError: (err: unknown) => {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as unknown as { response?: { data?: { message?: string } } })?.response?.data?.message)
        : 'Erro ao exportar CSV';
      error(errorMessage || 'Erro ao exportar CSV');
    },
  });
}

/**
 * Fetch skipped conversation details on demand
 */
export function useSkippedConversation(
  logId: string,
  protocol: string,
  enabled: boolean,
) {
  return useQuery<SkippedConversationDetail>({
    queryKey: [QUERY_KEY_LOGS, 'skipped-conversation', logId, protocol],
    queryFn: () => knowledgeApi.getSkippedConversation(logId, protocol).then((res) => res.data),
    enabled: enabled && !!logId && !!protocol,
    retry: 1,
  });
}
